import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Navigation from '../components/Navigation';
import LocationAutocomplete, { LocationData } from '../components/LocationAutocomplete';
import PublishLineupDialog from '../components/PublishLineupDialog';
import ConfirmAvailabilityModal from '../components/ConfirmAvailabilityModal';
import { Calendar, MapPin, Users, ChevronDown, ChevronRight, CheckCircle, XCircle, AlertCircle, HelpCircle, Clock, Check, ClipboardClock, X } from 'lucide-react';
import { formatMatchDateFull } from '../utils/dateFormatting';
import { MatchTypeIcon } from '../components/icons/MatchTypeIcon';
import DifficultyGauge from '../components/DifficultyGauge';
import axios from 'axios';
import { confirmLineupPosition, unconfirmLineupPosition } from '../services/lineupConfirmationService';
import { getDisplayName } from '../utils/displayName';

interface PlayerAvailability {
  id: number;
  player_id: number;
  availability_status: 'available' | 'unavailable' | 'maybe';
  notes?: string;
  player: {
    id: number;
    name: string;
    display_name?: string;
    tennisrecord_name?: string | null;
    email: string;
  };
}

interface TeamMember {
  id: number;
  user_id: number;
  active_status: boolean;
  is_captain?: boolean;
  user: {
    id: number;
    name: string;
    display_name?: string;
    tennisrecord_name?: string | null;
    email: string;
    gender?: 'male' | 'female' | null;
    ntrp_rating?: number | null;
  };
}

interface PlayerWithAvailability {
  id: number;
  player_id: number;
  name: string;
  display_name?: string;
  tennisrecord_name?: string | null;
  email: string;
  availability_status: 'available' | 'unavailable' | 'maybe' | null;
  notes?: string;
  gender?: 'male' | 'female' | null;
  ntrp_rating?: number | null;
}

interface LineupPlayer {
  id: number;
  name: string;
  display_name?: string;
  tennisrecord_name?: string | null;
  email: string;
}

interface LineupConfirmation {
  id: number;
  lineup_id: number;
  player_id: number;
  confirmed_at: string;
  confirmed_by: number;
}

interface LineupDifficulty {
  team_avg_rating: number;
  opponent_avg_rating: number;
  rating_diff: number;
  category: 'much-easier' | 'easier' | 'even' | 'harder' | 'much-harder';
}

interface LineupPosition {
  id: number;
  position_number: number;
  player1_id: number;
  player2_id?: number | null;
  player1: LineupPlayer;
  player2?: LineupPlayer | null;
  score?: string | null;
  won?: boolean | null;
  is_actual_result?: boolean;
  opponent_player1_name?: string | null;
  opponent_player2_name?: string | null;
  confirmations?: LineupConfirmation[] | null;
  difficulty?: LineupDifficulty | null;
}

interface LineupFormatPosition {
  id: number;
  position_number: number;
  match_type: string;
}

interface LineupFormat {
  id: number;
  total_lines: number;
  max_sets_per_line: number;
  positions: LineupFormatPosition[];
}

interface Team {
  id: number;
  name: string;
  lineup_format?: LineupFormat;
}

interface Match {
  id: number;
  team_id: number;
  opponent_name: string;
  match_date: string;
  location?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  match_type?: string;
  home_away?: string;
  notes?: string;
  lineup_published?: boolean;
  match_completed?: boolean;
  team?: Team;
}

interface MatchEditForm {
  opponent_name: string;
  match_date: string;
  match_time: string;
  location: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  home_away: string;
  notes: string;
}

// Helper function to determine if a position is singles
const isSinglesPosition = (matchType: string): boolean => {
  return matchType.includes('singles');
};

// Helper function to format match type display name
const formatMatchType = (matchType: string): string => {
  return matchType.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

const MatchDetailPage: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const { user } = useAuth();
  const toast = useToast();
  const [match, setMatch] = useState<Match | null>(null);
  const [availability, setAvailability] = useState<PlayerAvailability[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [lineup, setLineup] = useState<LineupPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<{ lineupId: number; playerId: number } | null>(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [showClearAvailabilityModal, setShowClearAvailabilityModal] = useState(false);
  const [pendingMatchUpdate, setPendingMatchUpdate] = useState<{ payload: any; availabilityCount: number } | null>(null);
  const [originalMatchData, setOriginalMatchData] = useState<MatchEditForm | null>(null);
  const [editForm, setEditForm] = useState<MatchEditForm>({
    opponent_name: '',
    match_date: '',
    match_time: '12:00',
    location: '',
    home_away: 'home',
    notes: '',
  });

  useEffect(() => {
    const fetchMatchData = async () => {
      try {
        if (!matchId) {
          setLoading(false);
          return;
        }
        
        // Fetch match details, availability, and lineup in parallel
        const [matchResponse, availabilityResponse] = await Promise.all([
          axios.get(`/matches/${matchId}`),
          axios.get(`/availability/matches/${matchId}/availability`)
        ]);
        
        const matchData = matchResponse.data;
        setMatch(matchData);
        setAvailability(availabilityResponse.data);
        
        // Fetch team members to get all players (including those without availability)
        if (matchData.team_id) {
          try {
            const teamResponse = await axios.get(`/teams/${matchData.team_id}`);
            const members = teamResponse.data.members || [];
            // Filter to only active members
            setTeamMembers(members.filter((m: TeamMember) => m.active_status));
          } catch (error) {
            console.error('Failed to fetch team members:', error);
            setTeamMembers([]);
          }
        }
        
        // Fetch lineup separately (it might not exist yet)
        // The API will return empty array if lineup not visible to this user
        try {
          const lineupResponse = await axios.get(`/lineups/matches/${matchId}/lineup`);
          const lineupData = lineupResponse.data || [];
          setLineup(lineupData);
          // Log for debugging
          if (lineupData.length === 0) {
            const isPast = new Date(matchData.match_date) < new Date();
            console.log(`No lineup returned for match ${matchId}. Match date: ${matchData.match_date}, Is Past: ${isPast}, Published: ${matchData.lineup_published}`);
          }
        } catch (lineupError: any) {
          // Log the error for debugging
          console.error(`Failed to fetch lineup for match ${matchId}:`, lineupError);
          // Lineup doesn't exist yet or access denied, that's okay
          setLineup([]);
        }
        
        // Populate edit form with current match data
        // Split match date into separate date and time components
        const matchDate = new Date(matchData.match_date);
        const formattedDate = `${matchDate.getFullYear()}-${String(matchDate.getMonth() + 1).padStart(2, '0')}-${String(matchDate.getDate()).padStart(2, '0')}`;
        const formattedTime = `${String(matchDate.getHours()).padStart(2, '0')}:${String(matchDate.getMinutes()).padStart(2, '0')}`;
        
        const formData = {
          opponent_name: matchData.opponent_name || '',
          match_date: formattedDate,
          match_time: formattedTime,
          location: matchData.location || '',
          formatted_address: matchData.formatted_address || undefined,
          latitude: matchData.latitude || undefined,
          longitude: matchData.longitude || undefined,
          home_away: matchData.home_away || 'home',
          notes: matchData.notes || '',
        };
        setEditForm(formData);
        setOriginalMatchData(formData);
      } catch (error) {
        console.error('Failed to fetch match data:', error);
        toast.error('Failed to load match data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchMatchData();
  }, [matchId, toast, user]);

  // Get current user's availability status
  const getUserAvailabilityStatus = (): 'available' | 'unavailable' | 'maybe' | null => {
    if (!user) return null;
    const userAvail = availability.find(avail => avail.player_id === user.id);
    return userAvail ? userAvail.availability_status : null;
  };


  // Check if a player has confirmed their lineup position
  const isPlayerConfirmed = (lineupPosition: LineupPosition, playerId: number): boolean => {
    if (!lineupPosition.confirmations) return false;
    return lineupPosition.confirmations.some(conf => conf.player_id === playerId);
  };

  // Get confirmation status indicator for a player (compact visual)
  const getConfirmationStatus = (lineupPosition: LineupPosition, playerId: number) => {
    const isConfirmed = isPlayerConfirmed(lineupPosition, playerId);
    if (isConfirmed) {
      return (
        <span title="Confirmed" className="inline-flex items-center ml-1.5">
          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
        </span>
      );
    }
    return (
      <span title="Not confirmed" className="inline-flex items-center ml-1.5">
        <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0"></div>
      </span>
    );
  };

  // Handle confirmation
  const handleConfirm = async (lineupId: number, playerId: number) => {
    if (!match || !user) return;
    
    const availabilityStatus = getUserAvailabilityStatus();
    const shouldSetAvailable = availabilityStatus !== 'available';
    
    // If user is not available, show modal to confirm and set as available
    if (shouldSetAvailable) {
      setPendingConfirmation({ lineupId, playerId });
      setShowConfirmModal(true);
      return;
    }
    
    // If already available, proceed with confirmation
    await performConfirmation(lineupId, playerId, false);
  };

  // Perform the actual confirmation
  const performConfirmation = async (lineupId: number, playerId: number, setAvailable: boolean) => {
    if (!match || !user) return;
    
    try {
      // Confirm lineup position
      await confirmLineupPosition(match.id, lineupId, playerId);
      
      // If user is not available, also set availability to available
      if (setAvailable) {
        try {
          await axios.post(`/availability/matches/${match.id}/availability`, {
            match_id: match.id,
            player_id: user.id,
            availability_status: 'available',
            notes: ''
          });
          
          // Refresh availability data
          const availabilityResponse = await axios.get(`/availability/matches/${match.id}/availability`);
          setAvailability(availabilityResponse.data);
        } catch (error) {
          console.error('Failed to set availability:', error);
          // Continue even if availability update fails - lineup is already confirmed
        }
      }
      
      toast.success('Lineup position confirmed' + (setAvailable ? ' and availability set to Available' : ''));
      
      // Refresh lineup data
      const response = await axios.get(`/lineups/matches/${match.id}/lineup`);
      setLineup(response.data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to confirm lineup position');
    }
  };

  // Handle modal confirm
  const handleModalConfirm = () => {
    if (pendingConfirmation) {
      performConfirmation(pendingConfirmation.lineupId, pendingConfirmation.playerId, true);
      setShowConfirmModal(false);
      setPendingConfirmation(null);
    }
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setShowConfirmModal(false);
    setPendingConfirmation(null);
  };

  // Handle unconfirmation
  const handleUnconfirm = async (lineupId: number, playerId: number) => {
    if (!match || !user) return;
    try {
      await unconfirmLineupPosition(match.id, lineupId, playerId);
      toast.success('Lineup position unconfirmed');
      // Refresh lineup data
      const response = await axios.get(`/lineups/matches/${match.id}/lineup`);
      setLineup(response.data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to unconfirm lineup position');
    }
  };

  // Check if current user is captain
  const isCurrentUserCaptain = (): boolean => {
    if (!user || !teamMembers.length) return false;
    const userMember = teamMembers.find(m => m.user_id === user.id);
    return userMember?.is_captain || false;
  };

  // Check if user can confirm/unconfirm a position (player for their own, captain for any)
  const canConfirmPosition = (playerId: number): boolean => {
    if (!user) return false;
    if (isCurrentUserCaptain()) return true;
    return user.id === playerId;
  };

  // Get availability status text
  const getAvailabilityText = (status: 'available' | 'unavailable' | 'maybe' | null): string => {
    if (status === 'available') return 'Available';
    if (status === 'unavailable') return 'Unavailable';
    if (status === 'maybe') return 'If Needed';
    return 'Not Set';
  };

  // Get availability badge classes
  const getStatusBadgeClass = (status: 'available' | 'unavailable' | 'maybe' | null): string => {
    if (status === 'available') return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700';
    if (status === 'maybe') return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700';
    if (status === 'unavailable') return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-300 dark:border-red-700';
    return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600';
  };

  // Helper function to check if a match is in the past
  const isMatchInPast = (matchDate: string): boolean => {
    return new Date(matchDate) < new Date();
  };

  // Group players by availability status
  const groupPlayersByStatus = (): {
    available: PlayerWithAvailability[];
    unavailable: PlayerWithAvailability[];
    maybe: PlayerWithAvailability[];
    notSet: PlayerWithAvailability[];
  } => {
    const grouped = {
      available: [] as PlayerWithAvailability[],
      unavailable: [] as PlayerWithAvailability[],
      maybe: [] as PlayerWithAvailability[],
      notSet: [] as PlayerWithAvailability[],
    };

    // Create a map of player_id to availability
    const availabilityMap = new Map<number, PlayerAvailability>();
    availability.forEach(avail => {
      availabilityMap.set(avail.player_id, avail);
    });

    // Process all team members
    teamMembers.forEach(member => {
      const avail = availabilityMap.get(member.user_id);
      const player: PlayerWithAvailability = {
        id: avail?.id || 0,
        player_id: member.user_id,
        name: getDisplayName(member.user),
        email: member.user.email,
        availability_status: avail ? avail.availability_status : null,
        notes: avail?.notes,
        gender: member.user.gender,
        ntrp_rating: member.user.ntrp_rating,
      };

      if (player.availability_status === 'available') {
        grouped.available.push(player);
      } else if (player.availability_status === 'unavailable') {
        grouped.unavailable.push(player);
      } else if (player.availability_status === 'maybe') {
        grouped.maybe.push(player);
      } else {
        grouped.notSet.push(player);
      }
    });

    return grouped;
  };

  // Toggle group expand/collapse
  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  // Check if group is expanded
  const isGroupExpanded = (groupName: string): boolean => {
    return expandedGroups.has(groupName);
  };

  // Get border color class for status group
  const getGroupBorderColor = (groupName: string): string => {
    switch (groupName) {
      case 'available':
        return 'border-l-green-500';
      case 'maybe':
        return 'border-l-yellow-500';
      case 'unavailable':
        return 'border-l-red-500';
      case 'notSet':
        return 'border-l-gray-400';
      default:
        return 'border-l-gray-400';
    }
  };

  // Get status icon component
  const getStatusIcon = (groupName: string) => {
    switch (groupName) {
      case 'available':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'unavailable':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'maybe':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'notSet':
        return <HelpCircle className="w-5 h-5 text-gray-400" />;
      default:
        return <HelpCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const handleAvailabilityChange = async (status: 'available' | 'unavailable' | 'maybe') => {
    if (!matchId || !user || !match) return;
    
    const currentStatus = getUserAvailabilityStatus();
    
    // If clicking the same status, toggle it off (unset)
    if (currentStatus === status) {
      await handleClearAvailability();
      return;
    }
    
    try {
      setUpdating(true);
      
      await axios.post(`/availability/matches/${matchId}/availability`, {
        match_id: parseInt(matchId),
        player_id: user.id,
        availability_status: status,
        notes: ''
      });
      
      // Refresh availability data
      const availabilityResponse = await axios.get(`/availability/matches/${matchId}/availability`);
      setAvailability(availabilityResponse.data);
      
      // Refresh team members to ensure we have the latest data
      if (match.team_id) {
        try {
          const teamResponse = await axios.get(`/teams/${match.team_id}`);
          const members = teamResponse.data.members || [];
          setTeamMembers(members.filter((m: TeamMember) => m.active_status));
        } catch (error) {
          console.error('Failed to refresh team members:', error);
        }
      }
      
      toast.success('Availability updated successfully!');
    } catch (error) {
      console.error('Failed to set availability:', error);
      toast.error('Failed to update availability. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  // Handle clearing availability
  const handleClearAvailability = async () => {
    if (!matchId || !user || !match) return;
    
    try {
      setUpdating(true);
      
      await axios.delete(`/teams/${match.team_id}/availability/${matchId}/${user.id}`);
      
      // Refresh availability data
      const availabilityResponse = await axios.get(`/availability/matches/${matchId}/availability`);
      setAvailability(availabilityResponse.data);
      
      // Refresh team members to ensure we have the latest data
      if (match.team_id) {
        try {
          const teamResponse = await axios.get(`/teams/${match.team_id}`);
          const members = teamResponse.data.members || [];
          setTeamMembers(members.filter((m: TeamMember) => m.active_status));
        } catch (error) {
          console.error('Failed to refresh team members:', error);
        }
      }
      
      toast.success('Availability cleared successfully!');
    } catch (error) {
      console.error('Failed to clear availability:', error);
      toast.error('Failed to clear availability. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleEditFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (locationData: LocationData | null) => {
    if (locationData) {
      setEditForm(prev => ({
        ...prev,
        location: locationData.name,
        formatted_address: locationData.formatted_address,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        location: '',
        formatted_address: undefined,
        latitude: undefined,
        longitude: undefined,
      }));
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!matchId) return;

    // Validation
    if (!editForm.opponent_name.trim()) {
      toast.error('Opponent name is required');
      return;
    }

    if (!editForm.match_date) {
      toast.error('Match date is required');
      return;
    }

    if (!editForm.match_time) {
      toast.error('Match time is required');
      return;
    }

    // Check if date, time, or location changed
    const hasDateChanged = originalMatchData && (
      originalMatchData.match_date !== editForm.match_date ||
      originalMatchData.match_time !== editForm.match_time
    );
    
    const hasLocationChanged = originalMatchData && (
      originalMatchData.location !== editForm.location ||
      originalMatchData.formatted_address !== editForm.formatted_address ||
      originalMatchData.latitude !== editForm.latitude ||
      originalMatchData.longitude !== editForm.longitude
    );

    const hasRelevantChange = hasDateChanged || hasLocationChanged;

    // If relevant fields changed and availability exists, prompt user
    if (hasRelevantChange && availability.length > 0) {
      // Combine date and time into datetime format
      const combinedDateTime = `${editForm.match_date}T${editForm.match_time}`;
      
      const payload = {
        opponent_name: editForm.opponent_name,
        match_date: combinedDateTime,
        location: editForm.location || undefined,
        formatted_address: editForm.formatted_address || undefined,
        latitude: editForm.latitude || undefined,
        longitude: editForm.longitude || undefined,
        home_away: editForm.home_away || undefined,
        notes: editForm.notes || undefined,
      };

      setPendingMatchUpdate({ payload, availabilityCount: availability.length });
      setShowClearAvailabilityModal(true);
      return;
    }

    // No relevant changes or no availability, proceed with update
    await performMatchUpdate({
      opponent_name: editForm.opponent_name,
      match_date: `${editForm.match_date}T${editForm.match_time}`,
      location: editForm.location || undefined,
      formatted_address: editForm.formatted_address || undefined,
      latitude: editForm.latitude || undefined,
      longitude: editForm.longitude || undefined,
      home_away: editForm.home_away || undefined,
      notes: editForm.notes || undefined,
    }, false);
  };

  const performMatchUpdate = async (payload: any, clearAvailability: boolean) => {
    if (!matchId) return;

    setSubmitting(true);
    try {
      // Clear availability if requested
      if (clearAvailability && availability.length > 0) {
        try {
          await axios.delete(`/availability/matches/${matchId}/availability`);
          // Refresh availability data
          const availabilityResponse = await axios.get(`/availability/matches/${matchId}/availability`);
          setAvailability(availabilityResponse.data);
        } catch (error) {
          console.error('Failed to clear availability:', error);
          // Continue with match update even if clearing availability fails
        }
      }

      const response = await axios.put(`/matches/${matchId}`, payload);
      
      setMatch(response.data);
      setIsEditing(false);
      
      // Update original match data
      const matchDate = new Date(response.data.match_date);
      const formattedDate = `${matchDate.getFullYear()}-${String(matchDate.getMonth() + 1).padStart(2, '0')}-${String(matchDate.getDate()).padStart(2, '0')}`;
      const formattedTime = `${String(matchDate.getHours()).padStart(2, '0')}:${String(matchDate.getMinutes()).padStart(2, '0')}`;
      
      const updatedFormData = {
        opponent_name: response.data.opponent_name || '',
        match_date: formattedDate,
        match_time: formattedTime,
        location: response.data.location || '',
        formatted_address: response.data.formatted_address || undefined,
        latitude: response.data.latitude || undefined,
        longitude: response.data.longitude || undefined,
        home_away: response.data.home_away || 'home',
        notes: response.data.notes || '',
      };
      setOriginalMatchData(updatedFormData);
      
      toast.success('Match updated successfully!');
    } catch (error: any) {
      console.error('Failed to update match:', error);
      
      // Handle Pydantic validation errors
      let errorMsg = 'Failed to update match';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        if (Array.isArray(detail)) {
          errorMsg = detail.map((err: any) => {
            const field = err.loc?.join('.') || 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        }
      }
      
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
      setShowClearAvailabilityModal(false);
      setPendingMatchUpdate(null);
    }
  };

  const handleClearAvailabilityConfirm = () => {
    if (pendingMatchUpdate) {
      performMatchUpdate(pendingMatchUpdate.payload, true);
    }
  };

  const handleClearAvailabilityCancel = async () => {
    if (pendingMatchUpdate) {
      // Save the match without clearing availability
      await performMatchUpdate(pendingMatchUpdate.payload, false);
    } else {
      setShowClearAvailabilityModal(false);
      setPendingMatchUpdate(null);
    }
  };

  const handlePublishLineup = async (sendEmail: boolean, emailNote: string | null, recipientType: string, recipientIds?: number[]) => {
    if (!matchId) return;
    
    try {
      const requestBody: any = {
        send_email: sendEmail,
        email_note: emailNote,
        recipient_type: recipientType
      };
      
      if (recipientType === 'specific_people' && recipientIds) {
        requestBody.recipient_ids = recipientIds;
      }
      
      await axios.put(`/lineups/matches/${matchId}/publish`, requestBody);
      
      // Refresh match data
      const matchResponse = await axios.get(`/matches/${matchId}`);
      setMatch(matchResponse.data);
      
      toast.success(sendEmail ? 'Lineup published and email sent successfully!' : 'Lineup published successfully!');
    } catch (error: any) {
      console.error('Failed to publish lineup:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to publish lineup. Please try again.';
      toast.error(errorMsg);
      throw error;
    }
  };


  const handleCancelEdit = () => {
    if (!match) return;
    
    // Reset form to current match data
    // Split match date into separate date and time components
    const matchDate = new Date(match.match_date);
    const formattedDate = `${matchDate.getFullYear()}-${String(matchDate.getMonth() + 1).padStart(2, '0')}-${String(matchDate.getDate()).padStart(2, '0')}`;
    const formattedTime = `${String(matchDate.getHours()).padStart(2, '0')}:${String(matchDate.getMinutes()).padStart(2, '0')}`;
    
    setEditForm({
      opponent_name: match.opponent_name || '',
      match_date: formattedDate,
      match_time: formattedTime,
      location: match.location || '',
      formatted_address: match.formatted_address || undefined,
      latitude: match.latitude || undefined,
      longitude: match.longitude || undefined,
      home_away: match.home_away || 'home',
      notes: match.notes || '',
    });
    
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Match not found</h2>
          <Link to="/matches" className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300">
            Back to matches
          </Link>
        </div>
      </div>
    );
  }

  // Check if lineup is visible to current user
  // Only show published lineups, even for captains/admins
  const isLineupVisible = (): boolean => {
    if (!match) return false;
    
    // Past matches are always visible
    if (isMatchInPast(match.match_date)) return true;
    
    // Future matches only visible if published (regardless of captain/admin status)
    return match.lineup_published === true;
  };

  // Check if user is in lineup (only if lineup is visible/published)
  const isUserInLineup = (): boolean => {
    if (!user || !lineup || !isLineupVisible()) return false;
    return lineup.some(
      position => position.player1_id === user.id || position.player2_id === user.id
    );
  };

  // Check if user has confirmed their lineup position
  const hasUserConfirmedLineup = (): boolean => {
    if (!user || !lineup || !isLineupVisible()) return false;
    return lineup.some(position => {
      if (position.player1_id === user.id) {
        return isPlayerConfirmed(position, user.id);
      }
      if (position.player2_id === user.id) {
        return isPlayerConfirmed(position, user.id);
      }
      return false;
    });
  };

  // Check if warning should be shown
  // Don't show warning if user has confirmed their lineup position
  const showWarning = match && isUserInLineup() && !isMatchInPast(match.match_date) && getUserAvailabilityStatus() !== 'available' && !hasUserConfirmedLineup();

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Match Info */}
          <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 md:p-6 mb-6">
            {!isEditing ? (
              <>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                      <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-0">
                        {match.home_away === 'away' ? 'at' : 'vs'} {match.opponent_name}
                      </h2>
                      {showWarning && (
                        <span className="inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700 self-start">
                          <svg className="w-3 h-3 md:w-4 md:h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          You're in the lineup, but not available
                        </span>
                      )}
                      {isUserInLineup() && !showWarning && !isMatchInPast(match.match_date) && (
                        <span className="inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-semibold bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-300 dark:border-green-700 self-start">
                          <svg className="w-3 h-3 md:w-4 md:h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          You're in the lineup
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  {match.team && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                      <Users className="w-4 h-4 flex-shrink-0" />
                      {match.team.name}
                    </p>
                  )}
                  {(() => {
                    const formatted = formatMatchDateFull(match.match_date);
                    return (
                      <>
                        <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                          <Calendar className="w-4 h-4 flex-shrink-0" />
                          {formatted.date}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                          <Clock className="w-4 h-4 flex-shrink-0" />
                          {formatted.time}
                        </p>
                      </>
                    );
                  })()}
                  {(match.location || match.formatted_address) && (
                    <div className="space-y-1">
                      {match.location && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          {match.location}
                        </p>
                      )}
                      {match.formatted_address && (
                        <p className={`flex items-center gap-2 ${match.location ? 'text-sm text-gray-600 dark:text-gray-400 ml-6' : 'text-sm text-gray-600 dark:text-gray-300'}`}>
                          {!match.location && <MapPin className="w-4 h-4 flex-shrink-0" />}
                          {match.formatted_address}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                {match.notes && (
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/50">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Notes</p>
                    <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{match.notes}</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Edit Match</h2>
                <form onSubmit={handleEditSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="opponent_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Opponent Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="opponent_name"
                        name="opponent_name"
                        value={editForm.opponent_name}
                        onChange={handleEditFormChange}
                        required
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Match Date & Time <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label htmlFor="match_date" className="sr-only">Match Date</label>
                          <input
                            type="date"
                            id="match_date"
                            name="match_date"
                            value={editForm.match_date}
                            onChange={handleEditFormChange}
                            required
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                          />
                        </div>
                        <div>
                          <label htmlFor="match_time" className="sr-only">Match Time</label>
                          <input
                            type="time"
                            id="match_time"
                            name="match_time"
                            value={editForm.match_time}
                            onChange={handleEditFormChange}
                            required
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Location
                      </label>
                      <LocationAutocomplete
                        id="location"
                        name="location"
                        value={editForm.location}
                        onChange={handleLocationChange}
                        placeholder="Search for a location..."
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Home/Away
                    </label>
                    <div className="flex space-x-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="home_away"
                          value="home"
                          checked={editForm.home_away === 'home'}
                          onChange={handleEditFormChange}
                          className="form-radio h-4 w-4 text-primary-600"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Home</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="home_away"
                          value="away"
                          checked={editForm.home_away === 'away'}
                          onChange={handleEditFormChange}
                          className="form-radio h-4 w-4 text-primary-600"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Away</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      rows={3}
                      value={editForm.notes}
                      onChange={handleEditFormChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 min-h-[80px]"
                    />
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-4 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>

          {/* Match Lineup */}
          <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 mb-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">Match Lineup</h3>
              {lineup.length > 0 && !match.lineup_published && !isMatchInPast(match.match_date) && (
                <button
                  onClick={() => {
                    setPublishDialogOpen(true);
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-primary-200 dark:border-primary-600 rounded-lg transition-colors"
                >
                  Publish Lineup
                </button>
              )}
            </div>

            {lineup.length > 0 && isLineupVisible() ? (
              <div className="space-y-2">
                {lineup.map((position) => {
                  // Get match type from team's lineup format
                  const formatPosition = match?.team?.lineup_format?.positions.find(
                    fp => fp.position_number === position.position_number
                  );
                  const matchType = formatPosition?.match_type || '';
                  const isSingles = matchType ? isSinglesPosition(matchType) : false;
                  
                  // Count how many positions have the same match type
                  const matchTypeCount = lineup.filter(p => {
                    const fp = match?.team?.lineup_format?.positions.find(
                      fpos => fpos.position_number === p.position_number
                    );
                    return fp?.match_type === matchType;
                  }).length;
                  
                  // Determine the index within positions of the same match type
                  const sameTypePositions = lineup.filter(p => {
                    const fp = match?.team?.lineup_format?.positions.find(
                      fpos => fpos.position_number === p.position_number
                    );
                    return fp?.match_type === matchType;
                  }).sort((a, b) => a.position_number - b.position_number);
                  const positionIndexWithinType = sameTypePositions.findIndex(
                    p => p.position_number === position.position_number
                  ) + 1;
                  
                  // Create the display label
                  const formattedType = formatMatchType(matchType);
                  const displayLabel = matchTypeCount > 1 
                    ? `${formattedType} ${positionIndexWithinType}`
                    : formattedType;

                  const hasScore = !!position.score;
                  const isWon = position.won === true;
                  const isLost = position.won === false;
                  const hasWonStatus = isWon || isLost;

                  // Check if opponents are set
                  const hasOpponents = position.opponent_player1_name || position.opponent_player2_name;

                  // Check if user is in this position
                  const userInPosition = user && (position.player1_id === user.id || position.player2_id === user.id);
                  // Check if user has confirmed this position
                  const userConfirmedPosition = userInPosition && isPlayerConfirmed(position, user.id);
                  // Check if warning condition applies (user in position + warning state)
                  const positionWarning = userInPosition && showWarning && !isMatchInPast(match.match_date);
                  // Check if match is in the past (no animation needed)
                  const isPast = isMatchInPast(match.match_date);
                  // Determine border classes - animate if in lineup but not confirmed (even in warning state), but only if match is not in the past
                  // For past matches, always show default border
                  const borderClasses = isPast
                    ? 'border border-gray-200 dark:border-gray-700'
                    : positionWarning
                    ? userConfirmedPosition
                      ? 'ring-2 ring-yellow-400 border-yellow-500 dark:border-yellow-500'
                      : 'animate-pulse-border-yellow ring-2 ring-yellow-400 border-yellow-500 dark:border-yellow-500'
                    : userInPosition
                    ? userConfirmedPosition
                      ? 'ring-2 ring-green-400 border-green-600 dark:border-green-600'
                      : 'animate-pulse-border-green ring-2 ring-green-400 border-green-600 dark:border-green-600'
                    : 'border border-gray-200 dark:border-gray-700';

                  return (
                    <div key={position.id} className={`${borderClasses} rounded-lg p-2.5 group transition-all`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 inline-flex items-center gap-1.5">
                          <MatchTypeIcon matchType={matchType} iconSize="w-4 h-4" />
                          {displayLabel}
                        </h4>
                        {hasScore && (
                          <span className="inline-flex items-center gap-2">
                            <span className={`text-xs font-medium ${
                              isWon ? 'text-green-600 dark:text-green-400' :
                              isLost ? 'text-red-600 dark:text-red-400' :
                              'text-gray-700 dark:text-gray-300'
                            }`}>
                              {position.score}
                            </span>
                            {position.difficulty && (
                              <DifficultyGauge
                                teamAvg={position.difficulty.team_avg_rating}
                                opponentAvg={position.difficulty.opponent_avg_rating}
                              />
                            )}
                          </span>
                        )}
                      </div>
                      <div className={`grid ${hasOpponents ? 'grid-cols-2' : 'grid-cols-1'} gap-4 text-sm`}>
                        {/* Left side - Our players */}
                        <div className="flex flex-col">
                          {isWon && (
                            <div className="mb-1.5">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-300 dark:border-green-700">
                                Won
                              </span>
                            </div>
                          )}
                          {!isWon && hasWonStatus && (
                            <div className="h-[28px]"></div>
                          )}
                          {!hasWonStatus && hasScore && (
                            <div className="h-[28px]"></div>
                          )}
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">
                                {isSingles ? 'Player:' : 'Player 1:'}
                              </span>
                              {position.player1?.name ? (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900 dark:text-gray-100">
                                    {getDisplayName(position.player1)}
                                  </span>
                                  {match?.lineup_published && !isMatchInPast(match.match_date) && position.player1_id && (
                                    <div className="flex items-center gap-1.5 ml-1">
                                      {getConfirmationStatus(position, position.player1_id)}
                                      {canConfirmPosition(position.player1_id) && (
                                        isPlayerConfirmed(position, position.player1_id) ? (
                                          <button
                                            onClick={() => handleUnconfirm(position.id, position.player1_id)}
                                            className="opacity-100 md:opacity-0 md:group-hover:opacity-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 rounded hover:bg-gray-50 dark:hover:bg-gray-900/20 transition-all"
                                            title="Unconfirm"
                                          >
                                            Unconfirm
                                          </button>
                                        ) : (
                                          <button
                                            onClick={() => handleConfirm(position.id, position.player1_id)}
                                            className="opacity-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 rounded hover:bg-gray-50 dark:hover:bg-gray-900/20 transition-all"
                                            title="Confirm"
                                          >
                                            Confirm
                                          </button>
                                        )
                                      )}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="font-medium text-gray-400 dark:text-gray-500 italic">
                                  TBD
                                </span>
                              )}
                            </div>
                            {!isSingles && (
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Player 2:</span>
                                {position.player2?.name ? (
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900 dark:text-gray-100">
                                      {getDisplayName(position.player2)}
                                    </span>
                                    {match?.lineup_published && !isMatchInPast(match.match_date) && position.player2_id && (() => {
                                      const player2Id = position.player2_id!; // Already checked above
                                      return (
                                        <div className="flex items-center gap-1.5 ml-1">
                                          {getConfirmationStatus(position, player2Id)}
                                          {canConfirmPosition(player2Id) && (
                                            isPlayerConfirmed(position, player2Id) ? (
                                              <button
                                                onClick={() => handleUnconfirm(position.id, player2Id)}
                                                className="opacity-100 md:opacity-0 md:group-hover:opacity-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 rounded hover:bg-gray-50 dark:hover:bg-gray-900/20 transition-all"
                                                title="Unconfirm"
                                              >
                                                Unconfirm
                                              </button>
                                            ) : (
                                              <button
                                                onClick={() => handleConfirm(position.id, player2Id)}
                                                className="opacity-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 rounded hover:bg-gray-50 dark:hover:bg-gray-900/20 transition-all"
                                                title="Confirm"
                                              >
                                                Confirm
                                              </button>
                                            )
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <span className="font-medium text-gray-400 dark:text-gray-500 italic">
                                    TBD
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Right side - Opponents (only show if opponents are set) */}
                        {hasOpponents && (
                          <div className="flex flex-col">
                            {isLost && (
                              <div className="mb-1.5">
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-300 dark:border-green-700">
                                  Won
                                </span>
                              </div>
                            )}
                            {!isLost && hasWonStatus && (
                              <div className="h-[28px]"></div>
                            )}
                            {!hasWonStatus && hasScore && (
                              <div className="h-[28px]"></div>
                            )}
                            <div className="space-y-1.5">
                              {isSingles ? (
                                position.opponent_player1_name && (
                                  <div>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Opponent:</span>
                                    <span className="font-medium text-gray-900 dark:text-gray-100">
                                      {position.opponent_player1_name}
                                    </span>
                                  </div>
                                )
                              ) : (
                                <>
                                  {position.opponent_player1_name && (
                                    <div>
                                      <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Opponent 1:</span>
                                      <span className="font-medium text-gray-900 dark:text-gray-100">
                                        {position.opponent_player1_name}
                                      </span>
                                    </div>
                                  )}
                                  {position.opponent_player2_name && (
                                    <div>
                                      <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Opponent 2:</span>
                                      <span className="font-medium text-gray-900 dark:text-gray-100">
                                        {position.opponent_player2_name}
                                      </span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : !isLineupVisible() && match && !isMatchInPast(match.match_date) ? (
              <div className="text-center py-8">
                <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-2">
                  <ClipboardClock className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">Lineup has not been published yet</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Please check back later.
                </p>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-2">
                  {!isMatchInPast(match.match_date) && !match.lineup_published ? (
                    <ClipboardClock className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                  ) : (
                    <svg className="h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  )}
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No lineup set</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  The lineup has not been set yet.
                </p>
              </div>
            )}
          </div>

          {/* My Availability */}
          {!isMatchInPast(match.match_date) && (
            <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-6 mb-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">My Availability</h3>
                  {getUserAvailabilityStatus() === null && (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadgeClass(getUserAvailabilityStatus())}`}>
                      {getAvailabilityText(getUserAvailabilityStatus())}
                    </span>
                  )}
                </div>
                
                {/* Availability Action Buttons */}
                <div className="grid grid-cols-3 gap-2">
                  {(() => {
                    const currentStatus = getUserAvailabilityStatus();
                    const isSelected = currentStatus === 'available';
                    const hasSelection = currentStatus !== null;
                    return (
                      <button
                        onClick={() => handleAvailabilityChange('available')}
                        disabled={updating}
                        className={`px-2.5 md:px-4 py-3 md:py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center gap-1.5 md:gap-2 whitespace-nowrap ${
                          isSelected
                            ? 'bg-green-600 text-white shadow-md ring-2 ring-green-300'
                            : hasSelection
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-600 opacity-50'
                            : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                        }`}
                      >
                        <CheckCircle className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-white' : hasSelection ? 'text-gray-400 dark:text-gray-500' : 'text-green-600 dark:text-green-500'}`} />
                        {updating ? 'Updating...' : 'Available'}
                      </button>
                    );
                  })()}
                  {(() => {
                    const currentStatus = getUserAvailabilityStatus();
                    const isSelected = currentStatus === 'maybe';
                    const hasSelection = currentStatus !== null;
                    return (
                      <button
                        onClick={() => handleAvailabilityChange('maybe')}
                        disabled={updating}
                        className={`px-2.5 md:px-4 py-3 md:py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center gap-1.5 md:gap-2 whitespace-nowrap ${
                          isSelected
                            ? 'bg-yellow-600 text-white shadow-md ring-2 ring-yellow-300'
                            : hasSelection
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-600 opacity-50'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
                        }`}
                      >
                        <AlertCircle className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-white' : hasSelection ? 'text-gray-400 dark:text-gray-500' : 'text-yellow-600 dark:text-yellow-500'}`} />
                        {updating ? 'Updating...' : 'If Needed'}
                      </button>
                    );
                  })()}
                  {(() => {
                    const currentStatus = getUserAvailabilityStatus();
                    const isSelected = currentStatus === 'unavailable';
                    const hasSelection = currentStatus !== null;
                    return (
                      <button
                        onClick={() => handleAvailabilityChange('unavailable')}
                        disabled={updating}
                        className={`px-2.5 md:px-4 py-3 md:py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center gap-1.5 md:gap-2 whitespace-nowrap ${
                          isSelected
                            ? 'bg-red-600 text-white shadow-md ring-2 ring-red-300'
                            : hasSelection
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-600 opacity-50'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50'
                        }`}
                      >
                        <XCircle className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-white' : hasSelection ? 'text-gray-400 dark:text-gray-500' : 'text-red-600 dark:text-red-500'}`} />
                        {updating ? 'Updating...' : 'Unavailable'}
                      </button>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Player Availability */}
          <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 md:p-6">
            <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-4">Player Availability</h3>

            {/* Grouped Players */}
            {(() => {
              const grouped = groupPlayersByStatus();
              
              const renderGroup = (
                groupName: string,
                players: PlayerWithAvailability[],
                label: string
              ) => {
                const isExpanded = isGroupExpanded(groupName);
                
                // Group players by gender
                const playersByGender = {
                  male: players.filter(p => p.gender === 'male').sort((a, b) => a.name.localeCompare(b.name)),
                  female: players.filter(p => p.gender === 'female').sort((a, b) => a.name.localeCompare(b.name)),
                  other: players.filter(p => p.gender !== 'male' && p.gender !== 'female').sort((a, b) => a.name.localeCompare(b.name)),
                };
                
                // Format gender breakdown for header
                const formatGenderBreakdown = () => {
                  const parts: string[] = [];
                  if (playersByGender.male.length > 0) {
                    parts.push(`${playersByGender.male.length} ${playersByGender.male.length === 1 ? 'man' : 'men'}`);
                  }
                  if (playersByGender.female.length > 0) {
                    parts.push(`${playersByGender.female.length} ${playersByGender.female.length === 1 ? 'woman' : 'women'}`);
                  }
                  if (playersByGender.other.length > 0) {
                    parts.push(`${playersByGender.other.length} unspecified`);
                  }
                  return parts.length > 0 ? `(${parts.join(', ')})` : '(0)';
                };
                
                return (
                  <div 
                    key={groupName} 
                    className={`bg-white dark:bg-gray-800 border-l-4 ${getGroupBorderColor(groupName)} border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden`}
                  >
                    <button
                      onClick={() => toggleGroup(groupName)}
                      className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        )}
                        {getStatusIcon(groupName)}
                        <span className="font-medium text-gray-900 dark:text-gray-100">{label}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">{formatGenderBreakdown()}</span>
                      </div>
                    </button>
                    
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-0 space-y-3">
                        {players.length === 0 ? (
                          <div className="p-3 text-sm text-gray-500 dark:text-gray-400 italic text-center">
                            No players in this category
                          </div>
                        ) : (
                          <>
                            {playersByGender.male.length > 0 && (
                              <div>
                                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-1">Men</h4>
                                <div className="space-y-2">
                                  {playersByGender.male.map((player) => (
                                    <div
                                      key={player.player_id}
                                      className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700/30"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <p className="font-medium text-gray-900 dark:text-gray-100">{player.name}</p>
                                            {player.ntrp_rating && (
                                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {player.ntrp_rating.toFixed(1)}
                                              </span>
                                            )}
                                          </div>
                                          {player.notes && (
                                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{player.notes}</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {playersByGender.female.length > 0 && (
                              <div>
                                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-1">Women</h4>
                                <div className="space-y-2">
                                  {playersByGender.female.map((player) => (
                                    <div
                                      key={player.player_id}
                                      className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700/30"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <p className="font-medium text-gray-900 dark:text-gray-100">{player.name}</p>
                                            {player.ntrp_rating && (
                                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {player.ntrp_rating.toFixed(1)}
                                              </span>
                                            )}
                                          </div>
                                          {player.notes && (
                                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{player.notes}</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {playersByGender.other.length > 0 && (
                              <div>
                                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-1">Not Specified</h4>
                                <div className="space-y-2">
                                  {playersByGender.other.map((player) => (
                                    <div
                                      key={player.player_id}
                                      className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700/30"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <p className="font-medium text-gray-900 dark:text-gray-100">{player.name}</p>
                                            {player.ntrp_rating && (
                                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {player.ntrp_rating.toFixed(1)}
                                              </span>
                                            )}
                                          </div>
                                          {player.notes && (
                                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{player.notes}</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <div className="space-y-3">
                  {renderGroup('available', grouped.available, 'Available')}
                  {renderGroup('maybe', grouped.maybe, 'If Needed')}
                  {renderGroup('unavailable', grouped.unavailable, 'Unavailable')}
                  {renderGroup('notSet', grouped.notSet, 'Not Set')}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
      
      {/* Publish Lineup Dialog */}
      {publishDialogOpen && match && (
        <PublishLineupDialog
          open={true}
          onClose={() => {
            setPublishDialogOpen(false);
          }}
          onPublish={async (sendEmail, emailNote, recipientType, recipientIds) => {
            await handlePublishLineup(sendEmail, emailNote, recipientType, recipientIds);
          }}
          isResend={false}
          match={match}
          lineup={lineup}
          teamMembers={teamMembers.map(m => ({
            id: m.user.id,
            name: getDisplayName(m.user),
            email: m.user.email || '',
            gender: m.user.gender,
            is_captain: m.is_captain || false,
            ntrp_rating: m.user.ntrp_rating
          }))}
        />
      )}

      {/* Confirm Availability Modal */}
      <ConfirmAvailabilityModal
        isOpen={showConfirmModal}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
      />

      {/* Clear Availability Confirmation Modal */}
      {showClearAvailabilityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Clear Availability?
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    You've changed the match date, time, or location. Would you like to clear all existing availability for this match?
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    {pendingMatchUpdate?.availabilityCount || 0} player{(pendingMatchUpdate?.availabilityCount || 0) !== 1 ? 's have' : ' has'} set their availability.
                  </p>
                </div>
                <button
                  onClick={handleClearAvailabilityCancel}
                  disabled={submitting}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleClearAvailabilityCancel}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                >
                  No, Keep Availability
                </button>
                <button
                  type="button"
                  onClick={handleClearAvailabilityConfirm}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                >
                  {submitting ? 'Updating...' : 'Yes, Clear Availability'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchDetailPage;
